/**
 * Password Validation Logic, and control interactivity.
 * @file
 */

export type PasswordValidator = (password: string) => boolean;

// MOHELA validation logic.

const passwordLengthValid: PasswordValidator = (p) => (p.length >= 8) && (p.length <= 25);
const passwordHasLowerCase: PasswordValidator = (p) => Boolean(p.match(/[a-z]/));
const passwordHasUpperCase: PasswordValidator = (p) => Boolean(p.match(/[A-Z]/));
const passwordHasNumber: PasswordValidator = (p) => Boolean(p.match(/\d/));
const passwordHasSpecial: PasswordValidator = (p) => Boolean(p.match(/[\.@\-_\$!\*&#]/));
const passwordDoesNotHaveInvalidChars: PasswordValidator =
  (p) => !p.match(/[^a-zA-Z0-9\.@\-_\$!\*&#]/);

/**
 * Determine if a password is valid, and apply appropriate styling.
 */
class PasswordValidatorUI {
  private static markInvalid(element: JQuery<HTMLElement>) {
    element.removeClass('valid').addClass('invalid').attr('title', 'criteria not met');
  }
  private static markValid(element: JQuery<HTMLElement>) {
    element.removeClass('invalid').addClass('valid').attr('title', 'criteria met');
  }
  private static markElement(valid: boolean, element: JQuery<HTMLElement>) {
    valid ? PasswordValidatorUI.markValid(element) : PasswordValidatorUI.markInvalid(element);
  }

  public static validatePassword(password: string): boolean {
    const lengthValid = passwordLengthValid(password);
    const hasLowerCase = passwordHasLowerCase(password);
    const hasUpperCase = passwordHasUpperCase(password);
    const hasNumber = passwordHasNumber(password);
    const hasSpecial = passwordHasSpecial(password);
    const noInvalid = passwordDoesNotHaveInvalidChars(password);

    const score = [hasLowerCase, hasUpperCase, hasNumber, hasSpecial]
      .filter((t) => t).length;
    const isValid = lengthValid && noInvalid && score >= 3;

    PasswordValidatorUI.markElement(lengthValid, $("[id$='length']"));
    PasswordValidatorUI.markElement(hasLowerCase, $("[id$='lowercase']"));
    PasswordValidatorUI.markElement(hasUpperCase, $("[id$='uppercase']"));
    PasswordValidatorUI.markElement(hasNumber, $("[id$='number']"));
    PasswordValidatorUI.markElement(hasSpecial, $("[id$='special']"));

    const validPasswordElement = $("[id$='pswd_criteria_met']");
    const invalidPasswordElement = $("[id$='pswd_criteria']");
    const invalidCharElement = $("[id$='pswd_invalid_character']");

    if (isValid) {
      validPasswordElement.show();
      invalidPasswordElement.hide();
      invalidCharElement.hide();
    } else if (noInvalid) {
      validPasswordElement.hide();
      invalidPasswordElement.show();
      invalidCharElement.hide();
    } else {
      // Invalid because special chars
      validPasswordElement.hide();
      invalidPasswordElement.hide();
      invalidCharElement.show();
    }

    return isValid;
  }
}

/**
* Determine if a password is valid, and apply appropriate styling.
*/
export const validatePassword = PasswordValidatorUI.validatePassword;

export function InitializePasswordCheckerControl() {
  const passwordInput = $("input[id$='txtPassword']");
  PasswordValidatorUI.validatePassword(String(passwordInput.val() ?? ""));
  passwordInput.on("keyup", (event) => {
    const target = event.target as HTMLInputElement;
    PasswordValidatorUI.validatePassword(target.value);
  });
}
